import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  User,
  signInWithPopup,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { User as UserType, CURRENT_TERMS_VERSION, getSignupCohort } from '@researcher/shared';
import { getAttribution } from '../utils/attribution';
import { Capacitor } from '@capacitor/core';
import { requestNotificationPermission } from '../utils/messaging';
import { initCapacitorPush } from '../utils/capacitorPush';
import { registerFcmToken, unregisterFcmToken, sendHeartbeat } from '../utils/notificationService';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserType | null;
  loading: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isDemoAccount: boolean;
  onboardingCompleted: boolean;
  acceptedTermsVersion: string | null;
  emailVerified: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changeName: (newName: string) => Promise<void>;
  changeEmail: (newEmail: string, currentPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  acceptTermsUpdate: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true); // default true to prevent flash for existing users
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState<string | null>(CURRENT_TERMS_VERSION); // default to current to prevent flash
  const [emailVerified, setEmailVerified] = useState(true); // default true to prevent flash for Google/existing users
  const fcmTokenRef = useRef<string | null>(null);
  const isInitialAuthCheck = useRef(true);

  // Register FCM token if user previously opted in
  const registerPushTokenIfOptedIn = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const prefs = userDoc.data()?.notificationPreferences;
      if (!prefs?.enabled) return;

      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Native: use Capacitor push
        const token = await initCapacitorPush();
        if (token) {
          fcmTokenRef.current = token;
          const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
          await registerFcmToken(token, platform as 'ios' | 'android');
        }
      } else {
        // Web: use Firebase Messaging
        if ('Notification' in window && Notification.permission === 'granted') {
          const result = await requestNotificationPermission();
          if (result.ok) {
            fcmTokenRef.current = result.token;
            await registerFcmToken(result.token, 'web');
          }
        }
      }
    } catch (error) {
      console.error('Error registering push token:', error);
    }
  };

  // Heartbeat on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentUser) {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Google OAuth users are always verified; email/password users must verify
        setEmailVerified(user.emailVerified || user.providerData[0]?.providerId === 'google.com');
        try {
          // Fetch or create/update user profile
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData: UserType = {
            id: user.uid,
            email: user.email || '',
            displayName: user.displayName || user.email || '',
            photoURL: user.photoURL || undefined
          };

          if (userDoc.exists()) {
            // Update existing profile with latest auth info (especially photoURL)
            const existingProfile = userDoc.data() as UserType;
            const needsUpdate =
              existingProfile.displayName !== userData.displayName ||
              existingProfile.email !== userData.email ||
              existingProfile.photoURL !== userData.photoURL;

            if (needsUpdate) {
              await setDoc(doc(db, 'users', user.uid), userData, { merge: true });
              // Preserve all existing Firestore fields (consent, preferences, etc.)
              // while updating with latest auth data
              setUserProfile({
                ...existingProfile,
                ...userData,
              });
            } else {
              // Use existing profile but ensure photoURL is available
              setUserProfile({
                ...existingProfile,
                photoURL: existingProfile.photoURL || userData.photoURL
              });
            }
            // undefined (old users) treated as complete
            setOnboardingCompleted(existingProfile.onboardingCompleted !== false);
            // null/undefined → needs re-acceptance (safe default for old users)
            setAcceptedTermsVersion(existingProfile.acceptedTermsVersion || null);
            setIsDemoAccount(existingProfile.demoAccount === true);
          } else {
            // Create new user profile
            try {
              const referredBy = sessionStorage.getItem('romy_referral_code') || undefined;
              const attribution = getAttribution();
              // Only set terms if user explicitly accepted (e.g. checked the
              // checkbox before signing up). OAuth users who skip the checkbox
              // will be caught by the TermsUpdateModal on next render.
              const explicitTerms = sessionStorage.getItem('explicit_terms_accepted') === 'true';
              await setDoc(doc(db, 'users', user.uid), {
                ...userData,
                onboardingCompleted: false,
                createdAt: serverTimestamp(),
                ...(explicitTerms ? {
                  termsAcceptedAt: serverTimestamp(),
                  acceptedTermsVersion: CURRENT_TERMS_VERSION,
                } : {}),
                milestones: { signed_up: serverTimestamp() },
                signupCohort: getSignupCohort(),
                attribution,
                ...(referredBy ? { referredBy } : {}),
              });
              if (explicitTerms) sessionStorage.removeItem('explicit_terms_accepted');
              if (referredBy) sessionStorage.removeItem('romy_referral_code');
              setUserProfile(userData);
              setOnboardingCompleted(false);
              setAcceptedTermsVersion(explicitTerms ? CURRENT_TERMS_VERSION : null);
            } catch (createError) {
              console.error('Error creating user profile:', createError);
              // Still set the profile from auth data even if Firestore write fails
              setUserProfile(userData);
            }
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          // If we can't load the profile, still set basic user data from auth
          setUserProfile({
            id: user.uid,
            email: user.email || '',
            displayName: user.displayName || user.email || '',
            photoURL: user.photoURL || undefined
          });
        }

        // Check role custom claims
        // Force refresh only on sign-in (not page reload) to avoid unnecessary
        // Firebase Auth network calls. The first onAuthStateChanged is always a
        // restore from cache; subsequent calls are real sign-in events.
        try {
          const forceRefresh = !isInitialAuthCheck.current;
          const tokenResult = await user.getIdTokenResult(forceRefresh);
          const role = tokenResult.claims.role as string | undefined;
          setIsAdmin(role === 'admin');
          setIsModerator(role === 'admin' || role === 'moderator');
        } catch {
          setIsAdmin(false);
          setIsModerator(false);
        }

        // Silently register push token if user previously opted in
        registerPushTokenIfOptedIn(user.uid);
        // Send heartbeat on app open
        sendHeartbeat();
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        setIsModerator(false);
        setIsDemoAccount(false);
        setOnboardingCompleted(true); // reset to default on logout
        setAcceptedTermsVersion(CURRENT_TERMS_VERSION); // reset to prevent flash
        setEmailVerified(true); // reset to prevent flash
      }
      isInitialAuthCheck.current = false;
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Native: use @capacitor-firebase/authentication for native Google Sign-In
        const result = await FirebaseAuthentication.signInWithGoogle();
        // Bridge to Firebase JS SDK using the ID token
        const idToken = result.credential?.idToken;
        if (idToken) {
          const credential = GoogleAuthProvider.credential(idToken);
          await signInWithCredential(auth, credential);
        }
      } else {
        // Web: use popup-based sign-in
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');
        await signInWithPopup(auth, provider);
      }
    } catch (error: any) {
      // Handle cancellation gracefully
      if (
        error.code === 'auth/cancelled-popup-request' ||
        error.code === 'auth/popup-closed-by-user' ||
        error.message?.includes('canceled') ||
        error.message?.includes('cancelled')
      ) {
        return;
      }
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        console.log('[AppleSignIn] Starting native Apple Sign-In...');
        const result = await FirebaseAuthentication.signInWithApple();
        console.log('[AppleSignIn] Got result, hasCredential:', !!result.credential, 'hasIdToken:', !!result.credential?.idToken);
        const idToken = result.credential?.idToken;
        const rawNonce = result.credential?.nonce;
        if (!idToken) {
          throw new Error('Apple Sign-In did not return a valid credential');
        }
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({ idToken, rawNonce });
        console.log('[AppleSignIn] Calling signInWithCredential...');
        await signInWithCredential(auth, credential);
        console.log('[AppleSignIn] Success!');
      } else {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        await signInWithPopup(auth, provider);
      }
    } catch (error: any) {
      console.error('[AppleSignIn] Error:', error.code, error.message);
      if (
        error.code === 'auth/cancelled-popup-request' ||
        error.code === 'auth/popup-closed-by-user' ||
        error.message?.includes('canceled') ||
        error.message?.includes('cancelled')
      ) {
        return;
      }
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    // Signal to onAuthStateChanged that terms were explicitly accepted
    sessionStorage.setItem('explicit_terms_accepted', 'true');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const referredBy = sessionStorage.getItem('romy_referral_code') || undefined;
    const attribution = getAttribution();
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      id: userCredential.user.uid,
      email,
      displayName,
      photoURL: null,
      onboardingCompleted: false,
      createdAt: serverTimestamp(),
      termsAcceptedAt: serverTimestamp(),
      acceptedTermsVersion: CURRENT_TERMS_VERSION,
      milestones: { signed_up: serverTimestamp() },
      signupCohort: getSignupCohort(),
      attribution,
      ...(referredBy ? { referredBy } : {}),
    });
    if (referredBy) sessionStorage.removeItem('romy_referral_code');
    setAcceptedTermsVersion(CURRENT_TERMS_VERSION);
    // Send verification email
    await sendEmailVerification(userCredential.user);
  };

  const resendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const changeName = async (newName: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    await updateProfile(user, { displayName: newName });
    await setDoc(doc(db, 'users', user.uid), { displayName: newName }, { merge: true });
    setUserProfile(prev => prev ? { ...prev, displayName: newName } : prev);
  };

  const changeEmail = async (newEmail: string, currentPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Not signed in');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await verifyBeforeUpdateEmail(user, newEmail);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Not signed in');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await firebaseUpdatePassword(user, newPassword);
  };

  const completeOnboarding = async () => {
    if (!currentUser) return;
    await setDoc(doc(db, 'users', currentUser.uid), {
      onboardingCompleted: true,
      milestones: { onboarding_completed: serverTimestamp() },
    }, { merge: true });
    setOnboardingCompleted(true);
  };

  const acceptTermsUpdate = async () => {
    if (!currentUser) return;
    await setDoc(doc(db, 'users', currentUser.uid), {
      acceptedTermsVersion: CURRENT_TERMS_VERSION,
      termsAcceptedAt: serverTimestamp(),
    }, { merge: true });
    setAcceptedTermsVersion(CURRENT_TERMS_VERSION);
  };

  const logout = async () => {
    // Unregister FCM token before signing out
    if (fcmTokenRef.current) {
      try {
        await unregisterFcmToken(fcmTokenRef.current);
      } catch (error) {
        console.error('Error unregistering FCM token:', error);
      }
      fcmTokenRef.current = null;
    }
    await signOut(auth);
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    isAdmin,
    isModerator,
    isDemoAccount,
    onboardingCompleted,
    acceptedTermsVersion,
    emailVerified,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    resendVerificationEmail,
    resetPassword,
    changeName,
    changeEmail,
    changePassword,
    acceptTermsUpdate,
    completeOnboarding,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

